import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Button,
  DataTable,
  EmptyState,
  PageHeader,
  Select,
  SkeletonTable,
  Tabs,
  type TabDef,
} from "@/components/ui";
import { useActiveMembership } from "@/hooks/use-company-context";
import { useIsCompanyAdmin } from "@/hooks/use-company-role";
import { useFormTemplates, type FormTemplate } from "@/hooks/use-company-form-templates";
import { useCompanyFormSubmissions } from "@/hooks/use-company-form-submissions";
import {
  emptyTemplateFilters,
  TemplateFilterBar,
  type TemplateFilterState,
} from "@/components/forms/template-filter-bar";
import { buildTemplatesColumns, statusToken } from "@/components/forms/templates-columns";
import { buildSubmissionsColumns } from "@/components/forms/submissions-columns";

export const Route = createFileRoute("/_company/company/forms/")({
  component: FormsPage,
});

type FormsTab = "templates" | "submissions";

function FormsPage() {
  const { t } = useTranslation(["company", "common"]);
  const navigate = useNavigate();
  const isAdmin = useIsCompanyAdmin();
  const active = useActiveMembership();
  const companyId = active?.company_id ?? "";

  const [tab, setTab] = useState<FormsTab>("templates");
  const [filters, setFilters] = useState<TemplateFilterState>(emptyTemplateFilters);
  const [submissionTemplate, setSubmissionTemplate] = useState<string>("");

  const templatesQuery = useFormTemplates();
  const submissionsQuery = useCompanyFormSubmissions(
    companyId,
    submissionTemplate ? { template_id: submissionTemplate } : {},
  );

  const allTemplates = useMemo(
    () => templatesQuery.data?.items ?? [],
    [templatesQuery.data?.items],
  );

  // Client-side filter over the fetched page — `listCompanyFormTemplates` takes
  // no server-side query params.
  const filteredTemplates = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return allTemplates.filter((tpl) => {
      if (q && !tpl.title.toLowerCase().includes(q) && !tpl.slug.toLowerCase().includes(q)) {
        return false;
      }
      if (filters.status && statusToken(tpl).key !== filters.status) return false;
      return true;
    });
  }, [allTemplates, filters]);

  const titleByTemplateId = useMemo(() => {
    const map = new Map<string, string>();
    for (const tpl of allTemplates) map.set(tpl.id, tpl.title);
    return map;
  }, [allTemplates]);

  const openDetail = (template: FormTemplate) => {
    void navigate({ to: "/company/forms/$templateId", params: { templateId: template.id } });
  };

  const templateColumns = useMemo(
    () => buildTemplatesColumns(t, openDetail),
    // navigate is referentially stable; t changes on locale switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );
  const submissionColumns = useMemo(
    () => buildSubmissionsColumns(t, (id) => titleByTemplateId.get(id)),
    [t, titleByTemplateId],
  );

  const tabs: TabDef<FormsTab>[] = [
    { value: "templates", label: t("forms.templatesTab"), count: allTemplates.length },
    { value: "submissions", label: t("forms.submissionsTab") },
  ];

  const submissions = submissionsQuery.data?.items ?? [];

  return (
    <div className="space-y-6 p-8">
      <PageHeader
        title={t("forms.title")}
        actions={
          /* A supplement is company IP: authoring is admin-only (the server
             answers a tester 403 not_company_admin), so a tester reads the
             families without the create CTA. */
          isAdmin ? (
            <Button onClick={() => void navigate({ to: "/company/forms/new" })}>
              {t("forms.newTemplate")}
            </Button>
          ) : undefined
        }
      />
      <p className="text-sm text-muted">{t("forms.bindingNote")}</p>

      <Tabs tabs={tabs} activeTab={tab} onTabChange={setTab} ariaLabel={t("forms.title")} />

      {tab === "templates" && (
        <div className="space-y-4" role="tabpanel">
          <TemplateFilterBar value={filters} onChange={setFilters} />
          {templatesQuery.isLoading ? (
            <SkeletonTable columns={5} rows={5} />
          ) : filteredTemplates.length === 0 ? (
            <EmptyState title={t("forms.empty")} />
          ) : (
            <DataTable
              columns={templateColumns}
              data={filteredTemplates}
              manualSorting={false}
              onRowClick={openDetail}
            />
          )}
        </div>
      )}

      {tab === "submissions" && (
        <div className="space-y-4" role="tabpanel">
          <div className="w-72">
            <label
              htmlFor="submissions-template"
              className="mb-1 block text-sm font-medium text-on-surface"
            >
              {t("forms.submissionColumns.template")}
            </label>
            <Select
              id="submissions-template"
              value={submissionTemplate}
              onChange={(e) => setSubmissionTemplate(e.target.value)}
              options={[
                { value: "", label: t("forms.filters.allTemplates") },
                ...allTemplates.map((tpl) => ({ value: tpl.id, label: tpl.title })),
              ]}
            />
          </div>
          {submissionsQuery.isLoading ? (
            <SkeletonTable columns={3} rows={5} />
          ) : submissions.length === 0 ? (
            <EmptyState title={t("forms.submissionsEmpty")} />
          ) : (
            <DataTable
              columns={submissionColumns}
              data={submissions}
              manualSorting={false}
              onRowClick={(row) =>
                void navigate({
                  to: "/company/forms/submissions/$submissionId",
                  params: { submissionId: row.id },
                })
              }
            />
          )}
        </div>
      )}
    </div>
  );
}
